import axios from 'axios';
import { customLogger, getHeaders } from '../utils/common';
import { ADMIN_ENDPOINTS } from './api/adminEndpoints';

const apiClient = 'web-product-analyzer';
const headers = () => {
  return {
    'Content-Type': 'application/json',
    authorization: getHeaders().Authorization,
    'api-client': apiClient
  };
};

export const getUserList = async () => {
  try {
    const response = await axios.get(ADMIN_ENDPOINTS.users(), { headers: headers() });
    return response.data;
  } catch (error) {
    customLogger('Error fetching user list:', error);
    return [];
  }
};

export const getUsersByRole = async (role: string) => {
  try {
    const response = await axios.get(ADMIN_ENDPOINTS.usersByRole(role), { headers: headers() });
    return response.data;
  } catch (error) {
    customLogger('Error fetching users by role:', error);
    return [];
  }
};

/**
 * Re-throws rather than swallowing to [] like its sibling list-fetchers above: a manager
 * with genuinely zero direct reports and a failed request would otherwise look identical
 * to the Pricing Manager history view, which needs to tell them apart to show an error
 * state instead of silently rendering an empty team (same reasoning as
 * getTeamAnalysisList in helper.ts).
 */
export const getDirectReports = async (managerUserId: number, role: string) => {
  try {
    const response = await axios.get(ADMIN_ENDPOINTS.directReports(managerUserId, role), {
      headers: headers()
    });
    return response.data;
  } catch (error) {
    customLogger('Error fetching direct reports:', error);
    throw error;
  }
};

export const getUserByEmail = async (email: string) => {
  try {
    const response = await axios.get(ADMIN_ENDPOINTS.userByEmail(email), { headers: headers() });
    return response.data;
  } catch (error) {
    customLogger('Error fetching user list:', error);
    throw error;
  }
};

export const createUser = async (userData: unknown) => {
  try {
    const response = await axios.post(ADMIN_ENDPOINTS.users(), userData, { headers: headers() });
    return response.data;
  } catch (error) {
    customLogger('Error creating user:', error);
    throw error;
  }
};

export const updateUser = async (userData: unknown) => {
  try {
    const response = await axios.put(ADMIN_ENDPOINTS.users(), userData, { headers: headers() });
    return response.data;
  } catch (error) {
    customLogger('Error updating user:', error);
    throw error;
  }
};

export const deleteUser = async (email: string) => {
  try {
    const response = await axios.delete(ADMIN_ENDPOINTS.users(), {
      headers: headers(),
      data: [email]
    });
    return response.data;
  } catch (error) {
    customLogger('Error deleting user:', error);
    throw error;
  }
};

export const getCropRegionList = async () => {
  try {
    const response = await axios.get(ADMIN_ENDPOINTS.cropsRegionsMasterData(), {
      headers: headers()
    });
    return response.data;
  } catch (error) {
    customLogger('Error fetching crop region list:', error);
    return [];
  }
};

export const getPersonaList = async () => {
  try {
    const response = await axios.get(ADMIN_ENDPOINTS.personas(), { headers: headers() });
    return response.data;
  } catch (error) {
    customLogger('Error fetching persona list:', error);
    return [];
  }
};

export const getRoleList = async () => {
  try {
    const response = await axios.get(ADMIN_ENDPOINTS.userRolesMasterData(), {
      headers: headers()
    });
    return response.data;
  } catch (error) {
    customLogger('Error fetching role list:', error);
    return [];
  }
};

export const createPersona = async (personaData: unknown) => {
  try {
    const response = await axios.post(ADMIN_ENDPOINTS.personas(), personaData, {
      headers: headers()
    });
    return response.data;
  } catch (error) {
    customLogger('Error creating persona:', error);
    throw error;
  }
};

export const updatePersona = async (personaData: unknown) => {
  try {
    const response = await axios.put(
      ADMIN_ENDPOINTS.personaById(personaData.persona_id),
      personaData,
      { headers: headers() }
    );
    return response.data;
  } catch (error) {
    customLogger('Error updating persona:', error);
    throw error;
  }
};

export const deletePersona = async (personaId: string) => {
  try {
    const response = await axios.delete(ADMIN_ENDPOINTS.personaById(personaId), {
      headers: headers()
    });
    return response.data;
  } catch (error) {
    customLogger('Error deleting persona:', error);
    throw error;
  }
};
